import * as React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { SelectChangeEvent } from "@mui/material/Select";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import { setTalentFilter } from "../../app/uiSlice";
import SelectSkills from "../../components/SelectSkills";
import useDebounce from "../../hooks/useDebounce";
import SelectVerticals from "../../components/SelectVerticals";
import SelectSimple from "../../components/SelectSimple";
import { UserWorkStatus } from "../../types/users";
import SelectState from "../../components/SelectState";
import SelectCertifications from "../../components/SelectCertification";
import { BaseModal, StyledTextField } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";
interface ModalTalentFilterProps {
  open: boolean;
  onClose: () => void;
}

// workStatuses will be created inside component to use hooks

const ModalTalentFilter = ({ open, onClose }: ModalTalentFilterProps) => {
  const filter = useAppSelector((state: RootState) => state.ui.talentFilter);
  const dispatch = useAppDispatch();
  const filterByLabel = useUILabel("common.filterBy");
  const resetLabel = useUILabel("common.reset");
  const closeLabel = useUILabel("common.close");
  const workStatusLabel = useUILabel("talent.workStatus");
  const stateLabel = useUILabel("common.state");
  const cityLabel = useUILabel("common.city");
  const partTimeProfessionalLabel = useUILabel("talent.partTimeProfessional");
  const soleProprietorLabel = useUILabel("talent.soleProprietor");
  const entrepreneurLabel = useUILabel("talent.entrepreneur");
  
  const workStatuses = React.useMemo(() => [
    { value: UserWorkStatus.PART_TIME, label: partTimeProfessionalLabel },
    { value: UserWorkStatus.SOLE_PROPRIETOR, label: soleProprietorLabel },
    { value: UserWorkStatus.ENTREPRENEUR, label: entrepreneurLabel },
  ], [partTimeProfessionalLabel, soleProprietorLabel, entrepreneurLabel]);

  const [city, setCity] = React.useState(filter.city);
  const [state, setState] = React.useState(filter.state);
  const [workStatus, setWorkStatus] = React.useState(filter.work_status);

  const handleReset = React.useCallback(() => {
    // TODO: still triggered double refresh
    setCity("");
    setState("");
    setWorkStatus("");
    dispatch(setTalentFilter({}));
  }, [dispatch]);

  const debounceCity = useDebounce(city, 500);
  const prevDebounceCityRef = React.useRef<string | undefined>(filter.city);

  React.useEffect(() => {
    if (debounceCity !== prevDebounceCityRef.current) {
      prevDebounceCityRef.current = debounceCity;
      dispatch(setTalentFilter({ city: debounceCity }));
    }
  }, [dispatch, debounceCity]);

  const footer = (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <Button onClick={handleReset} variant="outlined">
        {resetLabel}
      </Button>
      <Button variant="contained" color="success" onClick={onClose}>
        {closeLabel}
      </Button>
    </Stack>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={filterByLabel}
      footer={footer}
      maxWidth="xs"
      useGlassCard={true}
    >
      <Box sx={{ 
        p: 2.5, 
        display: "flex", 
        flexDirection: "column", 
        gap: 2.5,
        "@media (orientation: landscape) and (max-width: 900px)": {
          p: { xs: 1.5, sm: 2 },
          gap: { xs: 1.5, sm: 2 },
        },
      }}>
          <SelectVerticals
            onChange={(ids) =>
              dispatch(setTalentFilter({ business_verticals: ids }))
            }
            values={filter.business_verticals || []}
          />
          <SelectCertifications
            onChange={(ids) =>
              dispatch(setTalentFilter({ certificates: ids }))
            }
            values={filter.certificates || []}
          />
          <SelectSkills
            onChange={(ids) =>
              dispatch(setTalentFilter({ skills: ids }))
            }
            values={filter.skills || []}
          />
          <SelectSimple
            title={workStatusLabel}
            data={workStatuses}
            value={workStatus || ""}
          onChange={(e: SelectChangeEvent<string>) => {
              setWorkStatus(e.target.value);
              dispatch(setTalentFilter({ work_status: e.target.value }));
            }}
            sx={{ mt: 2 }}
          />
          <SelectState
            label={stateLabel}
            value={filter.state || ""}
            onChange={(value) =>
              dispatch(setTalentFilter({ state: value }))
            }
          />
        <StyledTextField
            label={cityLabel}
          value={city || ""}
            fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
          />
        </Box>
    </BaseModal>
  );
};
export default ModalTalentFilter;
